// R3 (renderer) — the block-level text renderer, shared by every surface that
// puts a `DocumentModel` on screen: `DocumentPane` (a whole model as its own
// scroll column) and the mobile interleaved column's original insets (a slice of
// one). One renderer, so the two can never drift apart on the details that other
// subsystems read back out of the DOM:
//
// - `data-block-id` on retelling paragraphs — anchor geometry, hard alignment,
//   `?loc=` reading-position tracking;
// - `data-start` on every block AND every emphasis run — its char offset into
//   `DocumentModel.text`, which is how `dom-offsets` maps a DOM Selection back
//   to model offsets for the gloss popover;
// - block-level `verse-line` elements for continuous-verse originals, so the
//   hard-align hook can inject a margin on a single hexameter;
// - context-link triggers wrap a link's runs and carry NO `data-start`, so the
//   runs stay the innermost offset elements and `anchor-picking`'s outermost
//   `data-start` is still the block's.
//
// Sizes are `em`-relative throughout: the surrounding container sets the base
// `font-size` in px and the whole column scales from it.

import { use, type ReactElement, type ReactNode } from "react";
import type {
  ContextLink,
  DocumentBlock,
  DocumentModel,
} from "@bindery/shared/corpus/document";
import type { PaneId } from "@bindery/shared/corpus/reader";
import { cn } from "@bindery/ui/lib/utils";
import { splitLinkedRuns, type LinkedRun } from "@bindery/reader-dom/runs";
import { ContextLinkTrigger } from "./context-link";
import { ContextLinkScopeContext } from "./context-link-scope";

interface DocumentBlocksProps {
  /** The model the blocks index into (`text`/`emphasis`/`headings` overlays). */
  model: DocumentModel;
  /** The blocks to render, in document order. Defaults to the whole model. */
  blocks?: readonly DocumentBlock[];
  /** Which side this run belongs to; the original reads as a quieter column. */
  pane: PaneId;
}

// The model carries a single `emphasis` overlay (no bold/italic
// discriminator), so every emphasized run renders as `<em>`; the run structure
// is ready for a strong overlay if the model ever gains one.
function renderLeafRuns(runs: readonly LinkedRun[]): ReactElement[] {
  return runs.map((run) =>
    run.em ? (
      <em key={run.start} data-start={run.start}>
        {run.value}
      </em>
    ) : (
      <span key={run.start} data-start={run.start}>
        {run.value}
      </span>
    ),
  );
}

interface Links {
  byId: ReadonlyMap<string, ContextLink>;
  model: DocumentModel;
}

/** Consecutive runs of one link, so a link spanning an emphasis edge gets one trigger. */
function groupByLink(runs: readonly LinkedRun[]): LinkedRun[][] {
  const groups: LinkedRun[][] = [];
  for (const run of runs) {
    const last = groups.at(-1);
    if (last && run.link !== null && last[0]?.link === run.link) last.push(run);
    else groups.push([run]);
  }
  return groups;
}

function renderRuns(
  runs: readonly LinkedRun[],
  links: Links | null,
): ReactNode[] {
  if (links === null) return renderLeafRuns(runs);
  return groupByLink(runs).flatMap((group) => {
    const first = group[0];
    const link = first?.link == null ? undefined : links.byId.get(first.link);
    if (!first || !link) return renderLeafRuns(group);
    return [
      <ContextLinkTrigger
        key={`link-${String(first.start)}`}
        link={link}
        model={links.model}
      >
        {renderLeafRuns(group)}
      </ContextLinkTrigger>,
    ];
  });
}

export function DocumentBlocks({
  model,
  blocks = model.blocks,
  pane,
}: DocumentBlocksProps) {
  const headingLevelByStart = new Map(
    model.headings.map((h) => [h.start, h.level]),
  );
  const isRetelling = pane === "retelling";
  const scope = use(ContextLinkScopeContext);
  const links: Links | null =
    scope === null || model.contextLinks.length === 0
      ? null
      : {
          byId: new Map(model.contextLinks.map((l) => [l.id, l])),
          model,
        };
  const linkSpans = links === null ? [] : model.contextLinks;

  // Verse-line ordinals for the marginal line numbers, counted over the WHOLE
  // model rather than the rendered `blocks` subset: the mobile interleaved
  // column renders slices of the original, and a count local to the slice
  // would restart at 1 in every inset instead of carrying the poem's own
  // numbering. Keyed by `start` because that is the one stable identity a
  // verse line has (originals carry no block ids).
  const verseLineNumberByStart = new Map<number, number>();
  {
    let line = 0;
    for (const b of model.blocks) {
      if (b.kind === "verse-line") verseLineNumberByStart.set(b.start, ++line);
    }
  }

  return blocks.map((block, i) => {
    const text = model.text.slice(block.start, block.end);
    if (block.kind === "heading") {
      const level = headingLevelByStart.get(block.start) ?? 1;
      return (
        <h1
          key={`h-${String(block.start)}`}
          data-start={block.start}
          aria-level={level}
          className={cn(
            "mb-8 font-serif font-semibold tracking-tight text-balance",
            isRetelling
              ? "text-[2.1em] leading-[1.1]"
              : "text-[1.5em] leading-[1.15] text-muted-foreground",
          )}
        >
          {text}
        </h1>
      );
    }
    const runs = splitLinkedRuns(
      model.text,
      block.start,
      block.end,
      model.emphasis,
      linkSpans,
    );
    if (block.kind === "verse-line") {
      // Continuous-verse original (e.g. the Odyssey): each metrical line is its
      // own block-level element — no `data-block-id` (originals carry no ids).
      // Tight verse leading with no inter-line gap, and a hanging indent so a
      // long hexameter that wraps tucks its continuation under the line's first
      // character. Block-level so the hard-align hook can inject `marginTop` on
      // it like any other block.
      //
      // Every 10th line carries its ordinal in the right margin, the classical
      // edition convention. Every verse line reserves the `pr` gutter (not just
      // the numbered ones) so a wrapped hexameter can never run under a number.
      // The numeral is decoration, not text: no `data-start` (dom-offsets must
      // not map a selection into it), `aria-hidden`, and `select-none`.
      const lineNumber = verseLineNumberByStart.get(block.start);
      const numbered = lineNumber !== undefined && lineNumber % 10 === 0;
      return (
        <p
          key={`v-${String(block.start)}`}
          data-start={block.start}
          // Deeper hanging indent below `md`: in a phone-width column nearly
          // every hexameter wraps, so at 1.6em the flush/indented alternation
          // stops reading as "line, continuation" and starts reading as noise.
          // The wide original pane keeps the tighter classical indent.
          //
          // `relative` only where a numeral hangs off the line: each positioned
          // line is its own paint layer, and a chapter's ~850 of them more than
          // doubled the hit testing Chrome runs after every keyboard scroll step.
          className={cn(
            "my-0 pr-[2.4em] pl-[2.6em] -indent-[2.6em] leading-[1.35] md:pl-[1.6em] md:-indent-[1.6em]",
            numbered && "relative",
          )}
        >
          {renderRuns(runs, links)}
          {numbered && (
            <span
              aria-hidden="true"
              className="text-muted-foreground/60 absolute top-[0.3em] right-0 indent-0 text-[0.7em] leading-none tabular-nums select-none"
            >
              {lineNumber}
            </span>
          )}
        </p>
      );
    }
    if (block.start === block.end) {
      // A scene-break divider (`---` in the source) flattens to a zero-length
      // paragraph: no prose, no runs, no anchors. It keeps its stable id and
      // `data-start`, so the geometry hooks still see one block box here —
      // rendered as the print convention's raised asterisk trio rather than
      // an invisible empty <p>. `select-none` keeps the ornament out of any
      // cross-paragraph selection the gloss popover would try to map back.
      return (
        <div
          key={block.id ?? `p-${String(block.start)}`}
          data-block-id={block.id}
          data-start={block.start}
          // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- <hr> is a void element; this scene break draws its glyphs as children
          role="separator"
          className="text-muted-foreground/80 mb-[1.1em] flex items-center justify-center gap-[1.1em] pt-[0.2em] font-serif text-[1em] select-none"
        >
          <span aria-hidden="true">*</span>
          <span aria-hidden="true">*</span>
          <span aria-hidden="true">*</span>
        </div>
      );
    }
    return (
      <p
        key={block.id ?? `p-${String(block.start)}`}
        data-block-id={block.id}
        data-start={block.start}
        className={cn(
          "mb-[1.1em]",
          // First body paragraph after the title gets no top gap.
          i <= 1 && "mt-0",
        )}
      >
        {renderRuns(runs, links)}
      </p>
    );
  });
}
